import { type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { ChartFrame } from '../../../components/chart/chart-frame';
import { FLOW_VAR, RAG_VAR } from '../../../components/chart/rag';
import { errorMessage } from '../../../lib/error-message';
import { reportApi, reportKeys } from '../api';

/**
 * Velocity theo sprint — spec 16.2.
 *
 * Không nhận `asOf`, và đó là chủ đích: velocity chỉ nói về sprint **đã đóng**,
 * mà một sprint đã đóng thì không đổi nữa. Gắn `asOf` vào đây là hứa một điều
 * biểu đồ không làm được.
 *
 * Đường trung bình chỉ vẽ khi có **từ hai sprint đã đóng**: dự báo bằng một
 * sprint duy nhất là đoán, không phải dự báo.
 */
export function VelocityChart({ projectId }: { projectId: string }): ReactNode {
  const { t } = useTranslation();

  const report = useQuery({
    queryKey: reportKeys.velocity(projectId),
    queryFn: () => reportApi.velocity(projectId),
  });

  const sprints = report.data?.sprints ?? [];
  const average = report.data?.averageDonePoints ?? null;
  const sayDo = report.data?.sayDoRatio ?? null;

  return (
    <ChartFrame
      title={t('reports.velocityTitle')}
      help="velocity"
      /*
        Say/do ratio đứng cạnh trung bình, không thay nó — APM-AGILE-a.
        Trung bình nói đội **làm được** bao nhiêu; tỉ lệ này nói họ **hứa** có
        sát không. Hai câu hỏi khác nhau, và một kế hoạch cần cả hai.
      */
      subtitle={[
        average === null
          ? t('reports.velocityNoAverage')
          : t('reports.velocityAverage', { points: average }),
        sayDo === null ? null : t('reports.sayDoRatio', { ratio: sayDo.toFixed(2) }),
      ]
        .filter((line) => line !== null)
        .join(' · ')}
      loading={report.isPending}
      error={report.isError ? errorMessage(report.error, t) : null}
      onRetry={() => void report.refetch()}
      empty={
        report.data && sprints.length === 0
          ? { title: t('reports.velocityEmpty'), description: t('reports.velocityEmptyHelp') }
          : null
      }
      legend={[
        { label: t('reports.donePoints'), color: FLOW_VAR.done },
        { label: t('reports.scopePoints'), color: FLOW_VAR.todo },
        { label: t('reports.committedPoints'), color: FLOW_VAR.inProgress },
      ]}
    >
      <div className="h-44 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={sprints}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
            <XAxis dataKey="name" tick={{ fontSize: 11 }} stroke="var(--color-border)" />
            <YAxis allowDecimals={false} tick={{ fontSize: 11 }} stroke="var(--color-border)" />
            <Tooltip
              contentStyle={{
                background: 'var(--color-background)',
                border: '1px solid var(--color-border)',
                fontSize: 12,
              }}
            />
            {/*
              ⚠️ Cột này **đổi nhãn** ở APM-AGILE-a. Nó luôn vẽ `totalPoints` —
              tức scope **hiện tại** — nhưng nhãn cũ ghi "Point đã cam kết", nên
              nó nói dối đúng chỗ quan trọng nhất: scope creep đọc ra thành "đội
              đã nhận nhiều thế mà". Cam kết thật giờ là một cột riêng.
            */}
            <Bar dataKey="totalPoints" name={t('reports.scopePoints')} fill={FLOW_VAR.todo} />
            <Bar
              dataKey="committedPoints"
              name={t('reports.committedPoints')}
              fill={FLOW_VAR.inProgress}
            />
            <Bar dataKey="donePoints" name={t('reports.donePoints')} fill={FLOW_VAR.done} />
            {average !== null && (
              <ReferenceLine
                y={average}
                stroke={RAG_VAR.red}
                strokeDasharray="4 4"
                label={{ value: `~${average}`, fontSize: 11, fill: RAG_VAR.red }}
              />
            )}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </ChartFrame>
  );
}
